// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the end-of-day wrap-up plans the cards show (design/evening-wrapup r2). The card in the
// Konvo chat and the corner's pop-up read the same plan by id from here, so ticking a row in one
// updates the other. While a started plan has agents working or waiting, the cards that show it
// look again now and then, so "Working · 4 min" and "Starts 18:00" stay true.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {WrapUpEdit, WrapUpPlan} from '@fluxer/schema/src/domains/konvo/WrapUp';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('KonvoWrapUp');

/** How often a shown, started plan is read again. */
export const LIVE_POLL_MS = 30_000;
/** Repeat reads within this window reuse the last answer (the chat card and the corner both ask). */
const FRESH_MS = 10_000;

export type WrapUpAction = 'start' | 'skip' | 'undo';

class WrapUpStore {
	plans = new Map<string, WrapUpPlan>();
	/** Plans that could not be read (gone, or never existed on this server). */
	missing = new Set<string>();
	/** The action in flight, by plan id. */
	busy = new Map<string, WrapUpAction | 'edit'>();
	/** The last action that failed, by plan id, for the card's one-line error. */
	failed = new Map<string, string>();
	/** The clock "Working · 4 min" counts from; ticks with each read. */
	now = Date.now();
	private fetchedAt = new Map<string, number>();
	private inflight = new Map<string, Promise<void>>();
	private watchers = new Map<string, {count: number; timer: ReturnType<typeof setInterval>}>();

	constructor() {
		makeAutoObservable<this, 'fetchedAt' | 'inflight' | 'watchers'>(this, {
			fetchedAt: false,
			inflight: false,
			watchers: false,
		});
	}

	get(id: string): WrapUpPlan | undefined {
		return this.plans.get(id);
	}

	private put(plan: WrapUpPlan): void {
		this.plans.set(plan.id, plan);
		this.missing.delete(plan.id);
		this.fetchedAt.set(plan.id, Date.now());
		this.now = Date.now();
	}

	load(id: string, options: {force?: boolean} = {}): Promise<void> {
		const at = this.fetchedAt.get(id) ?? 0;
		if (!options.force && this.plans.has(id) && Date.now() - at < FRESH_MS) return Promise.resolve();
		const running = this.inflight.get(id);
		if (running) return running;
		const promise = (async () => {
			try {
				const response = await http.get<{plan?: WrapUpPlan}>(Endpoints.KONVO_ASSISTANT_WRAP_UP(id));
				runInAction(() => {
					if (response.ok && response.body?.plan) this.put(response.body.plan);
					else if (response.status === 404) this.missing.add(id);
				});
			} catch (error) {
				logger.warn('Failed to load the wrap-up', error);
			} finally {
				runInAction(() => this.inflight.delete(id));
			}
		})();
		this.inflight.set(id, promise);
		return promise;
	}

	/** A card showing the plan: read it, and keep reading while its agents work. Returns the release. */
	watch(id: string): () => void {
		void this.load(id);
		const current = this.watchers.get(id);
		if (current) current.count += 1;
		else {
			const timer = setInterval(() => {
				const plan = this.plans.get(id);
				const live = plan?.status === 'started' && plan.agents.some((row) => isLive(row.state));
				const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
				if (live && visible) void this.load(id, {force: true});
				else runInAction(() => (this.now = Date.now()));
			}, LIVE_POLL_MS);
			this.watchers.set(id, {count: 1, timer});
		}
		return () => {
			const entry = this.watchers.get(id);
			if (!entry) return;
			entry.count -= 1;
			if (entry.count <= 0) {
				clearInterval(entry.timer);
				this.watchers.delete(id);
			}
		};
	}

	/**
	 * A new message in a shown plan's chat (Konvo answering "skip Felix" after changing the plan with
	 * edit_wrap_up): read the plan again so the card above shows the change.
	 */
	noteMessage(channelId: string): void {
		for (const [id, plan] of this.plans) {
			if (plan.channel_id === channelId && plan.status === 'offered' && this.watchers.has(id)) {
				void this.load(id, {force: true});
			}
		}
	}

	/** Tick, untick or move rows; the card changes at once and settles on the server's answer. */
	async edit(id: string, edit: WrapUpEdit): Promise<boolean> {
		const before = this.plans.get(id);
		if (before) this.plans.set(id, applyLocalEdit(before, edit));
		this.busy.set(id, 'edit');
		this.failed.delete(id);
		try {
			const response = await http.patch<{plan?: WrapUpPlan; message?: string}>(Endpoints.KONVO_ASSISTANT_WRAP_UP(id), {
				body: edit,
			});
			runInAction(() => {
				if (response.ok && response.body?.plan) this.put(response.body.plan);
				else {
					if (before) this.plans.set(id, before);
					this.failed.set(id, response.body?.message ?? 'edit');
				}
			});
			return response.ok;
		} catch (error) {
			logger.warn('Failed to change the wrap-up', error);
			runInAction(() => {
				if (before) this.plans.set(id, before);
				this.failed.set(id, 'edit');
			});
			return false;
		} finally {
			runInAction(() => this.busy.delete(id));
		}
	}

	async act(id: string, action: WrapUpAction): Promise<boolean> {
		if (this.busy.has(id)) return false;
		this.busy.set(id, action);
		this.failed.delete(id);
		try {
			const response = await http.post<{plan?: WrapUpPlan; message?: string}>(
				Endpoints.KONVO_ASSISTANT_WRAP_UP_ACTION(id, action),
			);
			runInAction(() => {
				if (response.ok && response.body?.plan) this.put(response.body.plan);
				else this.failed.set(id, response.body?.message ?? action);
			});
			// Acted on elsewhere meanwhile (the corner and the chat at once): show what it is now.
			if (!response.ok) void this.load(id, {force: true});
			return response.ok;
		} catch (error) {
			logger.warn('Failed to act on the wrap-up', error);
			runInAction(() => this.failed.set(id, action));
			return false;
		} finally {
			runInAction(() => this.busy.delete(id));
		}
	}
}

export function isLive(state: WrapUpPlan['agents'][number]['state']): boolean {
	return state === 'working' || state === 'scheduled' || state === 'queued';
}

/** Pure: the edit as the server will apply it, for the card to show at once. */
export function applyLocalEdit(plan: WrapUpPlan, edit: WrapUpEdit): WrapUpPlan {
	const change = (id: string) => edit.rows.find((row) => row.id === id);
	return {
		...plan,
		agents: plan.agents.map((row) => {
			const c = change(row.id);
			if (!c) return row;
			const machine = c.machine_id ? plan.machines.find((m) => m.id === c.machine_id) : undefined;
			return {
				...row,
				...(c.on == null ? {} : {on: c.on}),
				...(machine ? {machine_id: machine.id, machine_name: machine.name} : {}),
			};
		}),
		tasks: plan.tasks.map((row) => {
			const c = change(row.id);
			return c?.on == null ? row : {...row, on: c.on};
		}),
	};
}

export default new WrapUpStore();
